import { useEffect, useLayoutEffect, useMemo } from 'react';

import { useCart } from '../../hooks/useCart/useCart';
import { useHasLoggedOut } from '../AuthMachine/hooks/useHasLoggedOut';
import { useLastValidOrder } from '../Order/hooks/useLastValidOrder';
import { LastInteractedStoreModel } from './machine/LastInteractedStore.model';
import { useLastInteractedStoreMachine } from './useLastInteractedStoreMachine';

import type { LastInteractedStore } from './machine/LastInteractedStore.model';

/**
 * Tracks and synchronizes the last interacted stores based on the following sources:
 *
 * - active cart
 * - the most recently placed order
 *
 * NOTE: intended to be used just once each app session
 */
export const useTrackLastInteractedStore = () => {
  const [, send] = useLastInteractedStoreMachine();
  const hasLoggedOut = useHasLoggedOut();

  // ─── TRACK CHANGES ───────────────────────────────────────────────

  const cartStore = useCartStore();
  const lastPlacedOrderStore = useLastPlacedOrderStore();

  // ─── SYNC CHANGES ────────────────────────────────────────────────

  // NOTE: We use layout effects to prevent unexpected flickering while setting
  //       the last interacted store.

  useLayoutEffect(() => {
    send(SET_ACTIVE_CART_STORE(cartStore));
  }, [cartStore, send]);

  useLayoutEffect(() => {
    send(SET_LAST_PLACED_ORDER_STORE(lastPlacedOrderStore));
  }, [lastPlacedOrderStore, send]);

  useEffect(() => {
    if (hasLoggedOut) send(RESET_LAST_INTERACTED_MENU_STORE());
  }, [hasLoggedOut, send]);
};

// ─── HELPERS ────────────────────────────────────────────────────────────────────

const useCartStore = (): LastInteractedStore => {
  const { cart } = useCart();
  const { restaurant, deliveryOrderDetail } = cart ?? {};

  const restaurantId = restaurant?.id;
  const restaurantName = restaurant?.name;
  const restaurantSlug = restaurant?.slug;
  const addressId = deliveryOrderDetail?.address?.id;
  const deliveryFee = deliveryOrderDetail?.deliveryFee;
  const vendor = deliveryOrderDetail?.vendor;
  const vendorRestaurantId = deliveryOrderDetail?.vendorRestaurantId;

  return useMemo(() => {
    if (!restaurantId && !restaurantSlug) return;

    return {
      restaurantId,
      restaurantName,
      restaurantSlug,
      addressId,
      deliveryFee,
      vendor,
      vendorRestaurantId,
    };
  }, [
    addressId,
    deliveryFee,
    restaurantId,
    restaurantName,
    restaurantSlug,
    vendor,
    vendorRestaurantId,
  ]);
};

const useLastPlacedOrderStore = (): LastInteractedStore => {
  const { lastValidOrder: lastPlacedOrder } = useLastValidOrder();

  const restaurantId = lastPlacedOrder?.restaurant?.id;
  const restaurantName = lastPlacedOrder?.restaurant?.name;
  const restaurantSlug = lastPlacedOrder?.restaurant?.slug;
  const addressId = lastPlacedOrder?.deliveryOrderDetail?.address?.id;
  const deliveryFee = lastPlacedOrder?.deliveryOrderDetail?.deliveryFee;
  const vendor = lastPlacedOrder?.deliveryOrderDetail?.vendor;
  const vendorRestaurantId = lastPlacedOrder?.deliveryOrderDetail?.vendorRestaurantId;

  return useMemo(() => {
    if (!restaurantSlug && !addressId) return;

    return {
      restaurantId,
      restaurantName,
      restaurantSlug,
      addressId,
      deliveryFee,
      vendor,
      vendorRestaurantId,
    };
  }, [
    addressId,
    deliveryFee,
    restaurantId,
    restaurantName,
    restaurantSlug,
    vendor,
    vendorRestaurantId,
  ]);
};

// ────────────────────────────────────────────────────────────────────────────────

const { SET_ACTIVE_CART_STORE, SET_LAST_PLACED_ORDER_STORE, RESET_LAST_INTERACTED_MENU_STORE } =
  LastInteractedStoreModel.events;
